import type sv from '../sv/settings'

const settings: typeof sv = {
  nav: {
    plugins: 'Plugins',
    organization: 'Organisation',
    project: 'Project',
    members: 'Members',
    profile: 'Profile',
    notifications: 'Notifications',
  },
  dialog: {
    title: 'Settings',
    description: 'Account and project settings.',
    account: 'Account',
    back: 'Back to settings',
    close: 'Close settings',
  },
  role: {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
  },
  profile: {
    loading: 'Loading profile...',
    title: 'Settings',
    description: 'Update your name and phone number.',
    avatarAlt: 'Avatar',
    noImage: 'No image',
    avatarHint: 'Max 5 MB. JPG, PNG or WEBP.',
    uploadImage: 'Upload image',
    uploading: 'Uploading…',
    avatarInvalidType: 'Only JPG, PNG or WEBP are supported.',
    avatarTooLarge: 'The avatar file is too large (max 5 MB).',
    avatarUpdated: 'Avatar updated.',
    avatarUploadFailed: 'Could not upload the avatar. Please try again.',
    name: 'Name',
    namePlaceholder: 'Your name',
    phone: 'Phone',
    phonePlaceholder: '+46 ...',
    language: 'Language',
    languageHint: 'The interface language. Saved to your profile.',
    theme: 'Theme',
    themeHint: 'Light, dark or follow the system. Applies to this device.',
    voice: 'Read-aloud voice',
    voiceHint: 'The voice HUB uses to read a reply aloud. Applies to this device.',
    speed: 'Read-aloud speed',
    speedHint: 'Also applies to playback already in progress.',
    validation: 'Enter at least a name or a phone number.',
    saved: 'Profile updated.',
    saveFailed: 'Could not save the profile. Please try again.',
    saving: 'Saving...',
    save: 'Save profile',
  },
  browser: {
    title: 'Browser',
    description:
      "HUB's shared browser remembers your logins (cookies) between sessions, across projects. Clear them to sign out everywhere.",
    clear: 'Clear saved logins',
    clearing: 'Clearing…',
    cleared: 'Saved logins cleared. The next browser session starts signed out.',
    clearFailed: 'Could not clear. Try again.',
    confirmTitle: 'Clear saved logins?',
    confirmDescription:
      "You will be signed out of every site in HUB's browser, across all projects. The next browser session starts signed out.",
    cancel: 'Cancel',
    confirm: 'Clear',
  },
  push: {
    title: 'Notifications',
    description: 'Manage how you receive notifications',
    webPush: 'Web push notifications',
    webPushBody: 'Get notified even when HUB is not open in your browser.',
    unsupported: 'Your browser does not support web push notifications.',
    enabled: 'Enabled ✓',
    disable: 'Disable',
    disabling: 'Disabling...',
    enable: 'Enable web push notifications',
    enabling: 'Enabling...',
    test: 'Send test notification',
    testing: 'Sending...',
    testSent: 'Test notification sent – check your browser.',
    testNoSubscriptions: 'No subscription found on the server. Disable and enable again.',
    testFailed: 'Could not send the test notification. Please try again.',
    notConfigured: 'Web push notifications are not configured on the server.',
    permissionDenied:
      'The browser blocked notifications. Allow notifications for HUB in your browser settings.',
    subscribeFailed: 'Could not enable web push notifications. Please try again.',
    unsubscribeFailed: 'Could not disable web push notifications. Please try again.',
  },
  members: {
    loading: 'Loading members...',
    loadFailed: 'Could not load members.',
    inviteTitle: 'Invite to project',
    emailLabel: 'Email address',
    emailPlaceholder: 'name@company.com',
    roleLabel: 'Project role',
    invite: 'Invite',
    inviting: 'Inviting...',
    added: 'The user has been added to the project.',
    inviteSent: 'The invitation has been sent.',
    inviteFailed: 'Could not invite the user.',
    removeFailed: 'Could not remove the member.',
    revokeFailed: 'Could not revoke the invitation.',
    statusFailed: 'Could not change status.',
    roleFailed: 'Could not change role.',
    resendFailed: 'Could not resend the invitation.',
    resent: 'The invitation has been sent again.',
    title: 'Members',
    empty: 'No members yet.',
    you: '(you)',
    disabled: 'Disabled',
    guest: 'Guest',
    enable: 'Enable',
    disable: 'Disable',
    remove: 'Remove',
    pendingInvites: 'Pending invitations',
    invitedOn: 'Invited {{date}}',
    resend: 'Resend',
    revoke: 'Revoke',
  },
  project: {
    creditCapTitle: 'Credit cap',
    creditCapBody:
      'An optional ceiling on how many of the organization’s credits this project may spend. Leave empty for no cap.',
    creditCap: 'Cap (credits)',
    creditCapNone: 'No cap',
    creditCapValue: '{{credits}} credits',
    creditCapOrgLimit: 'Organization budget: {{credits}}',
    creditCapInvalid: 'Enter a whole number from 1, or leave the field empty.',
    creditCapOwnerOnly: 'Only the project owner can change the cap.',
    appearance: 'Appearance',
    projectImage: 'Project image',
    selectedIcon: 'Selected icon',
    defaultAppearance: 'Default appearance',
    uploading: 'Uploading...',
    uploadImage: 'Upload image',
    removeImage: 'Remove',
    imageHint: 'JPEG, PNG or WebP. Max 5 MB.',
    imageTooLarge: 'The image must be at most 5 MB.',
    imageInvalidType: 'Only JPEG, PNG and WebP are supported.',
    uploadFailed: 'Could not upload the image.',
    removeImageFailed: 'Could not remove the image.',
    pickIconWithImage: 'Choose an icon (shown when no image is uploaded)',
    pickIcon: 'Or choose an icon',
    details: 'Project details',
    name: 'Project name',
    description: 'Description',
    descriptionPlaceholder: 'Optional description...',
    saved: 'Project settings saved.',
    meetingNotesTitle: 'Meeting notes',
    meetingNotesBody:
      "How HUB should write meeting notes for this project. The instructions are added to every set of meeting notes HUB writes here — the live summary during a HUB meeting, the finished notes afterwards, and the notes from recorded Google Meet and Teams meetings. Empty = HUB's usual style.",
    meetingNotesLabel: 'Instructions for HUB',
    meetingNotesPlaceholder:
      'E.g. "Keep it short and factual. Always list decisions with an owner and a date. Use the headings Budget, Schedule and Safety when relevant. Never name subcontractors\' employees."',
    meetingNotesHint: '{{count}} / {{max}} characters',
    saveFailed: 'Could not save changes.',
    saving: 'Saving...',
    save: 'Save',
    transferTitle: 'Transfer project',
    transferBody:
      'Move the project to another organisation. Members who do not belong to the new organisation may lose access.',
    targetOrg: 'Target organisation',
    selectOrgPlaceholder: 'Select organisation...',
    transfer: 'Transfer',
    noEligibleOrgs: 'No other organisations available to transfer to.',
    transferConfirm:
      'Are you sure you want to move <strong>{{project}}</strong> to <strong>{{org}}</strong>? Members who do not belong to the new organisation may lose access.',
    transferring: 'Transferring...',
    transferSubmit: 'Transfer project',
    transferFailed: 'Could not transfer the project.',
    dangerZone: 'Danger zone',
    deleteTitle: 'Delete project',
    deleteBody:
      'Permanently deletes the project, including all chats, documents, drawings and tasks. This cannot be undone.',
    deleteButton: 'Delete project',
    deleteConfirm:
      'Type <strong>{{project}}</strong> to confirm the deletion. All data in the project will be permanently deleted.',
    deleting: 'Deleting...',
    deletePermanently: 'Delete permanently',
    deleteFailed: 'Could not delete the project.',
  },
  organization: {
    creditsTitle: 'Credits',
    creditsHint: 'Credits used this period. Only HUB admins can change the credit limit.',
    creditsMonthly: 'Period resets {{date}}',
    creditsManual: 'Counts until an admin resets it',
    loading: 'Loading...',
    loadFailed: 'Could not load organisation data.',
    loadFailedGeneric: 'Could not load organisation.',
    projectsWithLimit: 'Projects (limit: {{limit}})',
    totalMembers: 'Total members',
    authAny: 'Any',
    authMethod: 'Required sign-in method',
    settingsTitle: 'Settings',
    authHint: 'This setting takes effect at the next sign-in.',
    saved: 'Settings saved.',
    saveFailed: 'Could not save settings.',
    saving: 'Saving...',
    save: 'Save',
    membersTitle: 'Members',
    user: 'User',
    role: 'Organization role',
    guest: 'Guest',
    joined: 'Joined',
    disabled: 'Disabled',
    lastOwnerTooltip: 'Cannot change the role of the only owner',
    guestAdminTooltip: 'Owners and admins cannot be guests',
    yes: 'Yes',
    no: 'No',
    enable: 'Enable',
    disable: 'Disable',
    roleFailed: 'Could not change role.',
    statusFailed: 'Could not change status.',
    guestFailed: 'Could not change guest status.',
    membersHint: 'New members are added through project invitations.',
  },
  plugins: {
    intro:
      'Connect external MCP servers so HUB can use their tools in this project. Every plugin is used by all members of the project through HUB.',
    add: 'Add MCP',
    empty: 'No plugins yet.',
    emptyManager: 'Add an MCP server by its URL — HUB shows which tools it has.',
    readOnly: 'Only project owners and admins can change plugins.',
    notConfigured:
      'Plugins are not configured on this server yet (the encryption key is missing). Contact HUB support.',
    loadFailed: 'Could not load plugins.',
    status: {
      active: 'Active',
      needs_auth: 'Waiting for authorisation',
      needs_reauth: 'Needs re-authorisation',
      error: 'Error',
      disabled: 'Disabled',
    },
    toolCount_one: '{{count}} tool',
    toolCount_other: '{{count}} tools',
    showTools: 'Show tools',
    hideTools: 'Hide tools',
    noTools: 'The server has no tools.',
    toolUnavailable: 'unavailable (name too long)',
    authorizedBy: 'Authorised by {{name}}, {{date}}',
    formerMember: 'former member',
    apiKeyBy: 'API key added by {{name}}, {{date}}',
    noAuth: 'No sign-in',
    requiresAuth:
      'The server now requires authentication. Remove the plugin and add it again with an API key.',
    lastError: 'Last error: {{error}}',
    lastUsed: 'Last used {{date}}',
    enabled: 'Enabled',
    actions: 'Actions for {{name}}',
    refreshTools: 'Refresh tools',
    reauthorize: 'Re-authorise',
    rename: 'Rename',
    remove: 'Remove',
    renameTitle: 'Rename plugin',
    removeTitle: 'Remove {{name}}?',
    removeDescription:
      'HUB stops using the plugin’s tools right away and its stored credentials are deleted. This cannot be undone.',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    removed: 'The plugin was removed.',
    toolsRefreshed: 'Tools refreshed.',
    actionFailed: 'That did not work. Please try again.',
    connected: 'The plugin is connected.',
    connectFailed: 'The authorisation was not completed ({{code}}).',
    dialog: {
      title: 'Add MCP server',
      description: 'HUB connects to the server and reads which tools it has.',
      name: 'Name',
      namePlaceholder: 'e.g. Linear',
      url: 'URL',
      urlPlaceholder: 'https://mcp.example.com/mcp',
      apiKey: 'API key (optional)',
      apiKeyToggle: 'The server needs an API key',
      apiKeyHint: 'Sent as Authorization: Bearer … and stored encrypted.',
      identityTitle: 'Shared identity',
      identity:
        'Everything HUB does in the service is done as the account you sign in with or whose key you enter — also when another project member asks, and including writes the server allows. Use a service account or an account whose access you are happy to share with the whole project.',
      oauthNote: 'If the server needs a sign-in, you are sent on to approve the access.',
      submit: 'Add',
      submitting: 'Connecting…',
      errors: {
        INVALID_URL: 'This address is not allowed. Use a public https address without credentials.',
        LIMIT_REACHED: 'A project can have at most {{max}} plugins.',
        NOT_CONFIGURED: 'Plugins are not configured on this server.',
        PROBE_FAILED: 'Could not connect: {{message}}',
        unauthorized: 'The server refused the connection: {{message}}',
        FORBIDDEN: 'Only project owners and admins can add plugins.',
        generic: 'Could not add the plugin.',
      },
    },
  },
  roleInfo: {
    srLabel: 'Role information',
    title: 'Organisation roles',
    ownerTitle: 'Owner',
    ownerBody: 'Full control. Can create projects, manage all members and settings.',
    adminTitle: 'Admin',
    adminBody: 'Can create projects. Cannot change organisation settings or roles.',
    memberTitle: 'Member',
    memberBody: 'Standard access. Cannot create projects or manage other members.',
    guestTitle: 'Guest (guest flag)',
    guestBody:
      'Guests cannot create projects. They only have access to projects they have been explicitly invited to. Owners and admins are never guests.',
  },
}

export default settings
